import React from 'react';
import { AlertTriangle, Wifi, Server, RefreshCw, Home } from 'lucide-react';

/**
 * Error Boundary spécialisé pour les routes/pages
 * Fournit des messages d'erreur contextuels selon le type d'erreur
 */
export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorType: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Déterminer le type d'erreur
    let errorType = 'unknown';

    if (error.message?.includes('network') || error.message?.includes('fetch')) {
      errorType = 'network';
    } else if (error.message?.includes('401') || error.message?.includes('403')) {
      errorType = 'auth';
    } else if (error.message?.includes('500') || error.message?.includes('server')) {
      errorType = 'server';
    } else if (error.name === 'ChunkLoadError' || error.message?.includes('Loading chunk')) {
      errorType = 'chunk';
    }

    this.setState({ error, errorInfo, errorType });
  }

  getErrorConfig = () => {
    const { errorType } = this.state;

    switch (errorType) {
      case 'network':
        return {
          icon: Wifi,
          title: 'Problème de connexion',
          message: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.',
          actions: ['retry', 'home']
        };

      case 'auth':
        return {
          icon: AlertTriangle,
          title: 'Session expirée',
          message: 'Votre session a expiré. Veuillez vous reconnecter.',
          actions: ['login']
        };

      case 'server':
        return {
          icon: Server,
          title: 'Erreur serveur',
          message: 'Le serveur rencontre des difficultés. Nos équipes travaillent à résoudre le problème.',
          actions: ['retry', 'home']
        };

      case 'chunk':
        return {
          icon: RefreshCw,
          title: 'Mise à jour disponible',
          message: 'Une nouvelle version de l\'application est disponible. Actualisez la page.',
          actions: ['refresh']
        };

      default:
        return {
          icon: AlertTriangle,
          title: 'Erreur inattendue',
          message: 'Une erreur inattendue s\'est produite. Nos équipes ont été notifiées.',
          actions: ['retry', 'home']
        };
    }
  };

  handleRetry = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  handleGoLogin = () => {
    window.location.href = '/';
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const config = this.getErrorConfig();
      const Icon = config.icon;

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="flex justify-center mb-4">
              <Icon className="w-16 h-16 text-red-500" />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {config.title}
            </h2>

            <p className="text-gray-600 mb-6">
              {config.message}
            </p>

            <div className="space-y-3">
              {config.actions.includes('retry') && (
                <button
                  onClick={this.handleRetry}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Réessayer
                </button>
              )}

              {config.actions.includes('refresh') && (
                <button
                  onClick={this.handleRefresh}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Actualiser
                </button>
              )}

              {config.actions.includes('home') && (
                <button
                  onClick={this.handleGoHome}
                  className="w-full flex items-center justify-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Home className="w-4 h-4" />
                  Retour à l'accueil
                </button>
              )}

              {config.actions.includes('login') && (
                <button
                  onClick={this.handleGoLogin}
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Se connecter
                </button>
              )}
            </div>

            {/* Détails de l'erreur en développement */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Détails techniques (dev only)
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Wrapper pour envelopper facilement les routes avec un Error Boundary
 */
export const withRouteErrorBoundary = (Component, routeName) => {
  return (props) => (
    <RouteErrorBoundary>
      <Component {...props} />
    </RouteErrorBoundary>
  );
};

export default RouteErrorBoundary;